// Theme bridge between the React workbench and the Swift Cocoa+WebKit shell.
//
// The Swift app (SteerlyWorkbench.swift) registers a webkit message handler
// named "agentGuardTheme" and dispatches an `agentguard-native-theme` DOM
// CustomEvent whenever the user toggles theme from the macOS View menu.
//
// applyTheme() flips dataset.theme + localStorage in one place; toggleTheme()
// returns the new theme; postThemeToNative() forwards a theme change to the
// Swift menu so it stays in sync with in-page toggles.

type Theme = "light" | "dark";

interface WebKitMessageHandler {
  postMessage: (body: unknown) => void;
}

interface WebKitMessageHandlers {
  agentGuardTheme?: WebKitMessageHandler;
}

interface WebKitBridge {
  messageHandlers?: WebKitMessageHandlers;
}

interface WindowWithWebKit extends Window {
  webkit?: WebKitBridge;
}

const THEME_KEY = "agentguard-theme";

// WKWebView bug workaround — NOT a selector-matching problem (confirmed: a
// throwaway `:root[data-theme="light"] .activity-bar { background: yellow }`
// diagnostic DID fire correctly on toggle, proving WebKit re-matches the
// attribute selector fine). The bug is narrower: elements painted BEFORE the
// toggle don't get their INHERITED CUSTOM PROPERTY values (`var(--bg)`,
// `var(--activity-bg)`, …) recomputed, even though direct/literal
// declarations on newly-matched rules apply immediately. Freshly-mounted DOM
// (e.g. the Settings modal, opened after the toggle) is unaffected — it
// always renders correctly, which is what pointed at inheritance
// invalidation rather than rule matching.
//
// Fix: create a detached probe element, let CSS cascade compute each custom
// property fresh on it (new elements are never affected by this bug — same
// reason the modal works), then copy those resolved values onto :root as
// literal inline custom properties. Setting an inline style via JS is a
// distinct code path from an attribute-selector rule match, and reliably
// forces WebKit to invalidate + recompute every descendant that inherits
// the property — regardless of whether the staleness lives on :root itself
// or only in propagation to descendants.
//
// The property list is discovered at runtime (scanning same-origin
// stylesheets for `--*` declarations) instead of hardcoded, so it never
// drifts out of sync with apps/web/styles.css / refinements.css.
let cachedThemeVarNames: string[] | null = null;

function collectCustomProps(list: CSSRuleList, names: Set<string>): void {
  for (const rule of Array.from(list)) {
    if (rule instanceof CSSStyleRule) {
      for (const prop of Array.from(rule.style)) {
        if (prop.startsWith("--")) names.add(prop);
      }
    } else if ("cssRules" in rule && (rule as CSSGroupingRule).cssRules) {
      collectCustomProps((rule as CSSGroupingRule).cssRules, names);
    }
  }
}

function themeVarNames(): string[] {
  if (cachedThemeVarNames) return cachedThemeVarNames;
  const names = new Set<string>();
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      // Cross-origin stylesheet (e.g. Google Fonts) — cssRules access throws.
      continue;
    }
    collectCustomProps(rules, names);
  }
  cachedThemeVarNames = [...names];
  return cachedThemeVarNames;
}

function forceCustomPropertyRecompute(root: HTMLElement): void {
  const names = themeVarNames();
  if (!names.length) return;
  // Clear any inline overrides a PREVIOUS call left on root (Codex review,
  // PR #248 — verified against CSS cascade rules). root.style.setProperty()
  // below writes these as inline declarations, and an inline declaration
  // always wins over an attribute-selector rule for the same element,
  // REGARDLESS of what the attribute's current value is. Left uncleared,
  // root stays pinned to whichever theme was applied on the very first
  // call: the probe is a child of root, inherits root's own (stale, inline)
  // custom property values instead of the fresh `:root[data-theme="..."]`
  // stylesheet rule for the CURRENT attribute, and every toggle after the
  // first just echoes that first value back. Removing them first lets root
  // itself fall through to the stylesheet cascade for the current
  // data-theme during the synchronous measurement below — no visible
  // flash, since no paint happens between this removal and the re-set loop
  // finishing.
  for (const name of names) root.style.removeProperty(name);
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:absolute;visibility:hidden;pointer-events:none;width:0;height:0;overflow:hidden;";
  root.appendChild(probe);
  const computed = getComputedStyle(probe);
  for (const name of names) {
    const value = computed.getPropertyValue(name);
    if (value) root.style.setProperty(name, value);
  }
  root.removeChild(probe);
}

// Confirmed via the WKWebView inspector (Tauri build, Inspect Element):
// after forceCustomPropertyRecompute() runs, .workbench-shell's COMPUTED
// background-color is already correct (rgb(250, 251, 252) — the light --bg)
// while the actual on-screen pixels stay dark. So style resolution is fully
// correct at this point; only the PAINT is stale — WebKit resolved the new
// style but never invalidated the compositing layer to redraw it. Toggling
// `display` on <body> forces a full teardown/rebuild of the render tree
// (distinct from a layout-only reflow like reading offsetHeight), which
// reliably forces every backing layer to repaint from the now-correct
// computed styles.
function forceRepaint(): void {
  const body = document.body;
  const prevDisplay = body.style.display;
  body.style.display = "none";
  void body.offsetHeight;
  body.style.display = prevDisplay;
}

export function applyTheme(theme: string): Theme {
  const next: Theme = theme === "dark" ? "dark" : "light";
  const root = document.documentElement;
  root.dataset.theme = next;
  forceCustomPropertyRecompute(root);
  forceRepaint();
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch {
    // hardened webviews may block localStorage; the dataset attribute is enough.
  }
  return next;
}

export function toggleTheme(): Theme {
  const current = (document.documentElement.dataset.theme ||
    localStorage.getItem(THEME_KEY) ||
    "dark") as Theme;
  const next = applyTheme(current === "dark" ? "light" : "dark");
  postThemeToNative(next);
  return next;
}

export function postThemeToNative(theme: string): void {
  const bridge = (window as WindowWithWebKit).webkit?.messageHandlers?.agentGuardTheme;
  if (!bridge) return;
  try {
    bridge.postMessage(theme);
  } catch {
    // Swift handler not installed (running outside the .app); safe to ignore.
  }
}

// Reads the theme actually applied to the page right now (what applyTheme()
// last set), not what's stored in settings — the two can be momentarily out
// of sync during the initial-paint / settings-load handoff (see App.tsx).
export function getCurrentTheme(): Theme {
  return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}

// xterm.js takes its color theme as a plain object at construction time and
// on any later assignment to `term.options.theme` — it does NOT read CSS
// custom properties, so terminal panes previously stayed hard-coded dark
// regardless of the app-wide light/dark toggle. Mirrors the --bg/--ink/
// --accent tokens from apps/web/styles.css so terminals match the rest of
// the light theme instead of using the dark palette's blue on a white page.
export interface XtermTheme {
  background: string;
  foreground: string;
  cursor: string;
  cursorAccent: string;
  selectionBackground: string;
  // The 16 ANSI slots are optional: the DARK theme leaves them unset on
  // purpose — xterm's built-in ANSI defaults are dark-background-tuned and
  // are exactly what shipped before theming existed. The LIGHT theme MUST
  // override them (Codex review, PR #253): those same defaults render
  // `white` as #e5e5e5 and `brightWhite` as #ffffff, which is invisible on
  // the #fafbfc light background the moment a CLI styles its output.
  black?: string;
  red?: string;
  green?: string;
  yellow?: string;
  blue?: string;
  magenta?: string;
  cyan?: string;
  white?: string;
  brightBlack?: string;
  brightRed?: string;
  brightGreen?: string;
  brightYellow?: string;
  brightBlue?: string;
  brightMagenta?: string;
  brightCyan?: string;
  brightWhite?: string;
}

const XTERM_THEMES: Record<Theme, XtermTheme> = {
  dark: {
    background: "#0B0C0E",
    foreground: "#E8EAED",
    cursor: "#7BB1E0",
    cursorAccent: "#0B0C0E",
    selectionBackground: "rgba(123, 177, 224, 0.28)",
  },
  light: {
    background: "#fafbfc",
    foreground: "#14181d",
    cursor: "#2c63eb",
    cursorAccent: "#fafbfc",
    selectionBackground: "rgba(44, 99, 235, 0.18)",
    // Light-background ANSI palette (VS Code Light+ terminal set). Some
    // slots (yellows, greens, dim white) are still inherently low-contrast
    // on white — that's handled at render time by minimumContrastRatio: 4.5
    // in the Terminal constructors (TerminalPane.tsx / terminal.js), the
    // same enforcement VS Code pairs with this palette. See the interface
    // comment for why light must override these while dark keeps xterm's
    // defaults.
    black: "#000000",
    red: "#cd3131",
    green: "#00bc00",
    yellow: "#949800",
    blue: "#0451a5",
    magenta: "#bc05bc",
    cyan: "#0598bc",
    white: "#555555",
    brightBlack: "#666666",
    brightRed: "#cd3131",
    brightGreen: "#14ce14",
    brightYellow: "#b5ba00",
    brightBlue: "#0451a5",
    brightMagenta: "#bc05bc",
    brightCyan: "#0598bc",
    brightWhite: "#a5a5a5",
  },
};

export function getXtermTheme(theme: Theme = getCurrentTheme()): XtermTheme {
  return XTERM_THEMES[theme];
}

// Terminal panes call this once per mount to stay in sync with theme changes
// that happen after they're already open (in-app toggle, native menu, "system"
// switching) — applyTheme() only sets a DOM attribute, it doesn't dispatch an
// event, so a MutationObserver on that attribute is the one place that sees
// every theme change regardless of where it originated.
export function subscribeThemeChange(onChange: (theme: Theme) => void): () => void {
  const target = document.documentElement;
  const observer = new MutationObserver(() => onChange(getCurrentTheme()));
  observer.observe(target, { attributes: true, attributeFilter: ["data-theme"] });
  return () => observer.disconnect();
}
